import { useState, useEffect, useRef, useCallback } from 'react';
import RouteAnnouncer from '../layout/RouteAnnouncer';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useIncidentStore } from '@/store/incident.store';
import { useTenantQuery } from '@/hooks/useTenantQuery';
import { api } from '@/services/api';
import type { Incident, IncidentStatus } from '@/types/incident';
import { STATUS_COLORS } from '@/types/incident';
import FieldBottomNav from './FieldBottomNav';
import FieldDrawer from './FieldDrawer';
import SyncStatusIndicator from './SyncStatusIndicator';
import GpsTracker from './GpsTracker';
import MauiUpgradePrompt from './MauiUpgradePrompt';
import { ArrowLeftIcon } from '../icons';
import { useCapacitorKeyboard } from '@/hooks/useCapacitorKeyboard';
import Button from '../common/Button';
import FaIcon from '../common/FaIcon';

const INCIDENT_STATUS_LABELS: Record<IncidentStatus, string> = {
  planned: 'Planned',
  active: 'Active',
  stabilizing: 'Stabilizing',
  recovery: 'Recovery',
  closed: 'Closed',
  archived: 'Archived',
};

export default function FieldLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeIncident, activeIncidentId, isDailyOps, _hasHydrated, setActiveIncident } = useIncidentStore();
  const { isKeyboardVisible } = useCapacitorKeyboard();

  // Hydration timeout fallback (same as RequireIncident)
  const [hydrationTimedOut, setHydrationTimedOut] = useState(false);
  useEffect(() => {
    if (_hasHydrated) return;
    const timer = setTimeout(() => setHydrationTimedOut(true), 2000);
    return () => clearTimeout(timer);
  }, [_hasHydrated]);

  const effectivelyHydrated = _hasHydrated || hydrationTimedOut;
  const needsIncident = !activeIncident || isDailyOps;

  const { data: incidentResponse, isLoading: isLoadingIncidents } = useTenantQuery<{ data: Incident[] }>(
    ['incidents', 'field-incident-selector'],
    '/incidents',
    {
      params: { status: 'active,stabilizing,recovery,planned', limit: '10' },
      enabled: needsIncident && effectivelyHydrated,
    }
  );
  const availableIncidents = incidentResponse?.data ?? [];
  const [confirmingExit, setConfirmingExit] = useState(false);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cooldownUntilRef = useRef<number>(0);

  const handleBackClick = useCallback(() => {
    if (confirmingExit) {
      // Second tap — actually exit
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
      setConfirmingExit(false);
      navigate('/');
    } else if (Date.now() < cooldownUntilRef.current) {
      // User already saw confirmation recently — exit directly
      navigate('/');
    } else {
      // First tap — arm confirmation
      setConfirmingExit(true);
      exitTimerRef.current = setTimeout(() => {
        setConfirmingExit(false);
        // Skip confirmation for the next 10s — user already saw it
        cooldownUntilRef.current = Date.now() + 10000;
      }, 3000);
    }
  }, [confirmingExit, navigate]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, []);

  // Determine if current route is the map
  const isMapRoute = location.pathname === '/field';

  // Show spinner while hydrating
  if (!effectivelyHydrated || (activeIncidentId && !activeIncident && !isDailyOps)) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-900">
        <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // No incident selected — show inline selector within the field chrome
  if (needsIncident) {
    return (
      <div className="fixed inset-0 z-0 flex flex-col bg-gray-900 text-white" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <header className="shrink-0 flex items-center h-12 px-3 gap-3 bg-gray-900">
          <Button onClick={() => navigate('/')} variant="ghost" size="sm" className="-ml-1">
            <ArrowLeftIcon className="h-5 w-5" />
          </Button>
          <h1 className="text-sm font-semibold">Field Ops</h1>
        </header>
        <div className="flex-1 overflow-y-auto flex items-start justify-center px-4 pt-8">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <div className="mx-auto w-16 h-16 bg-amber-900/40 rounded-full flex items-center justify-center mb-5">
                <FaIcon icon="triangle-exclamation" className="w-8 h-8 text-amber-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Select an Incident to Start</h2>
              <p className="text-gray-400 text-sm">Choose an active incident to begin field operations.</p>
            </div>

            {isLoadingIncidents ? (
              <div className="space-y-3 mb-6">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="animate-pulse bg-gray-800 rounded-lg h-16" />
                ))}
              </div>
            ) : availableIncidents.length > 0 ? (
              <div className="space-y-2 mb-6">
                {availableIncidents.map((incident) => (
                  <button
                    key={incident.id}
                    onClick={async () => {
                      try {
                        const fullIncident = await api.get<Incident>(`/incidents/${incident.id}`);
                        setActiveIncident(fullIncident);
                      } catch {
                        // Fall back to the list object if the fetch fails
                        setActiveIncident(incident);
                      }
                      // Persist to the server like IncidentSwitcher does. user_contexts is
                      // the durable record of the engaged incident and is reconciled on
                      // boot, so an engage that only touches the local store is dropped on
                      // the next load. Field responders reload constantly on flaky links,
                      // which is exactly where losing the engaged incident hurts most.
                      api.put('/user/context', {
                        incident_id: incident.id,
                        is_daily_ops: false,
                      }).catch(() => {});
                    }}
                    className="w-full text-left bg-gray-800 hover:bg-gray-700 active:bg-gray-600 rounded-lg p-4 flex items-center justify-between touch-manipulation transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white truncate">{incident.name}</p>
                      <p className="font-mono text-xs text-gray-400 mt-0.5">{incident.incident_number}</p>
                    </div>
                    <span className={`ml-3 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[incident.status].bg} ${STATUS_COLORS[incident.status].text}`}>
                      {INCIDENT_STATUS_LABELS[incident.status]}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center mb-6 py-6 bg-gray-800 rounded-lg border border-gray-700">
                <p className="text-sm text-gray-400 mb-3">No active incidents found.</p>
                <Link
                  to="/incidents/new"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                >
                  Create Incident
                </Link>
              </div>
            )}

            <div className="text-center">
              <Link to="/incidents" className="text-sm text-primary-400 hover:text-primary-300">
                View All Incidents
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-0 flex flex-col bg-gray-100 dark:bg-gray-950"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <RouteAnnouncer />
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:bg-primary-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-md focus:text-sm">
        Skip to main content
      </a>
      {/* Top Bar */}
      <header className="shrink-0 z-30 bg-gray-900 dark:bg-gray-900 text-white select-none">
        <div className="flex items-center h-12 px-3 gap-3">
          <Button
            onClick={handleBackClick}
            variant={confirmingExit ? 'danger' : 'ghost'}
            size="sm"
            className="-ml-1"
          >
            {confirmingExit ? (
              <span className="text-xs font-semibold px-1">Exit?</span>
            ) : (
              <ArrowLeftIcon className="h-5 w-5" />
            )}
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold truncate">
              Field Ops
            </h1>
            {activeIncident && (
              <p className="text-xs text-gray-300 truncate">
                {activeIncident.name}
              </p>
            )}
          </div>
          <SyncStatusIndicator />
        </div>
      </header>

      {/* GPS Tracking indicator (shows only when tracking is active) */}
      <GpsTracker />

      {/* MAUI cutover (P4-02/P4-03): pilot invitation or update prompt, native bridge only */}
      <MauiUpgradePrompt />

      {/* Content — different behavior for map vs other routes */}
      <main
        id="main-content"
        tabIndex={-1}
        className={isMapRoute ? 'flex-1 relative' : 'relative flex-1 overflow-y-auto bg-surface-secondary'}
        style={{ paddingBottom: isKeyboardVisible ? '0px' : 'calc(4rem + var(--safe-area-bottom, env(safe-area-inset-bottom, 0px)))' }}
      >
        <Outlet />
        {isMapRoute && <FieldDrawer />}
      </main>

      {/* Bottom Nav — hidden when keyboard is visible to maximize form space */}
      {!isKeyboardVisible && <FieldBottomNav />}
    </div>
  );
}
